/*
 * Copyright (c) Nexign, JSC, 2021.
 */
/**
 * Campaign Info modal activate
 *
 * @author Yakov Gushchin
 * @version 1.13.0
 * @since 1.12.0
 */

import { useCallback } from 'react';

import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { Button, LinkBlock } from '../../../components/common';
import { ImgStatusError, ImgStatusSuccess } from '../../../components/common/Block/Img';
import {
    makeSelectPageCampaignInfoTranslation,
    makeSelectTranslationCommon,
} from '../../../modules/internationalization/intSelectors';
import { ICommonTranslation } from '../../../modules/internationalization/types';
import { IPageCampaignInfoTranslation } from '../../../modules/internationalization/types/pages/campaignInfo';
import Modal from '../../../modules/modal/components/Modal';
import { ModalCenterStyled } from '../../../modules/modal/components/styled/ModalCenterStyled';
import { EModalStatus, IModalRenderProps } from '../../../modules/modal/types/modalTypes';
import ButtonServiceUserDenied from '../../../modules/role/components/ButtonServiceUserDenied';
import { guardComponentType } from '../../../modules/role/reducer/guardReducer';
import { makeSelectGuardComponents } from '../../../modules/role/selectors/guardSelector';
import { IRootReducer } from '../../../reducers/rootReducer';
import { RoutePath } from '../../../routes/constants/routesConstants';
import { getPageInquiryInfoUrl, isPageInquiryInfoExternalUrl } from '../../PageInquiryInfo/utils/getInquiryInfoUrl';
import { campaignInfoActivateStartAction } from '../campaignInfoActions';
import { ICampaignInfoReducer } from '../campaignInfoReducer';
import { makeSelectCampaignInfoActivateResult } from '../campaignInfoSelectors';
import { ICampaignInfoGeneral } from '../types/campaignInfoTypes';
import { CampaignInfoModalActivateStyled } from './styled/CampaignInfoModalActivateStyled';

type TCampaignInfoModalActivate = Pick<IModalRenderProps, 'onCloseModal'> & Pick<ICampaignInfoGeneral, 'type'>;

const CampaignInfoModalActivate = (props: TCampaignInfoModalActivate & IConnectedProps & IDispatchProps) => {
    const {
        activateResult,
        componentsFeature,
        type,
        translationCampaignInfo,
        translationCommon,

        activate,
        onCloseModal,
    } = props;

    const getModalStartTranslation = useCallback(() =>
            translationCampaignInfo[type].modalApply[EModalStatus.start],
        [translationCampaignInfo, type]);

    const getModalSuccessTranslation = useCallback(() =>
            translationCampaignInfo.common.modalApply[EModalStatus.success],
        [translationCampaignInfo]);

    const getModalFailureTranslation = useCallback(() =>
            translationCampaignInfo.common.modalApply[EModalStatus.failure],
        [translationCampaignInfo]);

    return (
        <Modal dataName="campaign-info-active-modal" width="480px">
            <CampaignInfoModalActivateStyled>

                <Modal.Status status={EModalStatus.start}>
                    <Modal.Title>{getModalStartTranslation().title}</Modal.Title>
                    <Modal.Text>{getModalStartTranslation().text}</Modal.Text>
                    <Modal.Control>
                        <ButtonServiceUserDenied dataName="campaign-info-active-modal-btn-send"
                                                 kind="medium"
                                                 onClick={activate}>
                            {translationCommon.modal.send}
                        </ButtonServiceUserDenied>
                        <LinkBlock dataName="campaign-info-active-modal-btn-cancel" onClick={onCloseModal}>
                            {translationCommon.modal.close}
                        </LinkBlock>
                    </Modal.Control>
                </Modal.Status>

                <Modal.Status status={EModalStatus.failure}>
                    <ModalCenterStyled>
                        <ImgStatusError/>
                        <Modal.Title>
                            {getModalFailureTranslation().text}
                        </Modal.Title>
                        <Modal.Control>
                            <Button dataName="campaign-info-active-modal-btn-cancel"
                                    kind="medium"
                                    onClick={onCloseModal}>
                                {translationCommon.modal.accessibly}
                            </Button>
                        </Modal.Control>
                    </ModalCenterStyled>
                </Modal.Status>

                <Modal.Status status={EModalStatus.success}>
                    <ModalCenterStyled>
                        <ImgStatusSuccess/>
                        <Modal.Title>
                            {getModalSuccessTranslation().textInquiryIdStart}&nbsp;
                            <LinkBlock
                                isExternalUrl={isPageInquiryInfoExternalUrl(componentsFeature)}
                                url={getPageInquiryInfoUrl(componentsFeature,
                                    activateResult.campaignInquiryId.toString())}
                            >
                                № {activateResult.campaignInquiryId}
                            </LinkBlock>&nbsp;
                            {getModalSuccessTranslation().textInquiryIdEnd}
                        </Modal.Title>
                        <Modal.Text>
                            {getModalSuccessTranslation().textInquiryStart}<br/>
                            <LinkBlock url={RoutePath.inquiries}>
                                {translationCommon.createInquiry.inquiryHistory}
                            </LinkBlock>
                        </Modal.Text>
                        <Modal.Control>
                            <Button kind="medium" onClick={onCloseModal}>
                                {translationCommon.modal.good}
                            </Button>
                        </Modal.Control>
                    </ModalCenterStyled>
                </Modal.Status>

            </CampaignInfoModalActivateStyled>
        </Modal>
    );
};

export interface IConnectedProps extends Pick<ICampaignInfoReducer, 'activateResult'> {
    componentsFeature: guardComponentType;
    translationCampaignInfo: IPageCampaignInfoTranslation;
    translationCommon: ICommonTranslation;
}

export const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    activateResult: makeSelectCampaignInfoActivateResult(),
    componentsFeature: makeSelectGuardComponents(),
    translationCampaignInfo: makeSelectPageCampaignInfoTranslation(),
    translationCommon: makeSelectTranslationCommon(),
});

export interface IDispatchProps {
    activate: () => void;
}

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    activate: () => dispatch(campaignInfoActivateStartAction()),
});

export default connect(mapStateToProps, mapDispatchToProps)(CampaignInfoModalActivate);
